const Pulse = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse rounded-sm bg-gray-200 ${className}`} />
);

// Mirrors SubscriptionList layout
function SubscriptionSkeleton() {
  return (
    <div className="app-container space-y-5 bg-white">
      {/* header row */}
      <div className="mx-auto flex w-full max-w-7xl items-center justify-between">
        <Pulse className="h-8 w-48" />
        <div className="hidden items-center gap-5 md:flex">
          <Pulse className="h-8 w-32" />
          <Pulse className="h-8 w-24" />
        </div>
      </div>

      {/* info card */}
      <div className="grid h-full w-full grid-cols-2 gap-6 rounded-xl bg-white p-4 md:flex">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-2">
            <Pulse className="h-3 w-16" />
            <Pulse className="h-5 w-28" />
          </div>
        ))}
      </div>

      {/* mobile action buttons */}
      <div className="flex w-full items-center gap-4 md:hidden">
        <Pulse className="h-9 flex-1" />
        <Pulse className="h-9 flex-1" />
      </div>
    </div>
  );
}

// Mirrors DashboardProductList layout
function ProductListSkeleton() {
  return (
    <div className="inline-flex w-full flex-col items-center justify-start gap-20 bg-[#FAF6F2] px-4 py-5 md:py-8 xl:px-0">
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-start gap-6">
        {/* header */}
        <div className="flex w-full flex-wrap items-center gap-4">
          <Pulse className="h-8 w-48" />
          <Pulse className="h-6 w-20 rounded-full" />
        </div>

        {/* 2-column grid */}
        <div className="w-full grid-cols-2 flex-col items-start gap-4 md:grid lg:gap-6">
          {/* Left – plan cards */}
          <div className="flex flex-col gap-4">
            {/* bundle card */}
            <div className="flex flex-col gap-3">
              <Pulse className="h-4 w-20" />
              <Pulse className="h-36 w-full rounded-xl" />
            </div>
            {/* individual plans */}
            <div className="flex flex-col gap-2">
              <Pulse className="h-4 w-40" />
              <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
                {Array.from({ length: 2 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-4"
                  >
                    <div className="flex flex-col gap-2">
                      <Pulse className="h-5 w-32" />
                      <Pulse className="h-4 w-48" />
                    </div>
                    <Pulse className="h-8 w-20" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right – add-ons */}
          <div className="flex flex-col gap-3 pt-4 md:pt-0">
            <Pulse className="h-4 w-16" />
            {Array.from({ length: 2 }).map((_, i) => (
              <Pulse key={i} className="h-28 w-full rounded-xl" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Mirrors PaymentAndBilling layout
function PaymentAndBillingSkeleton() {
  return (
    <div className="app-container space-y-6">
      <Pulse className="h-8 w-56" />

      <div className="flex flex-col gap-6 rounded-xl bg-white p-6">
        {/* Payment methods row */}
        <div className="flex flex-col gap-5 md:flex-row md:items-center">
          <div className="flex-1 space-y-3">
            <Pulse className="h-6 w-40" />
            <div className="flex items-center gap-3">
              <Pulse className="h-10 w-[58px]" />
              <div className="flex flex-col gap-2">
                <Pulse className="h-4 w-32" />
                <Pulse className="h-3 w-24" />
              </div>
            </div>
          </div>
          <Pulse className="h-9 w-40" />
        </div>

        {/* Billing & Shipping row */}
        <div className="flex flex-col gap-5 md:flex-row md:items-center">
          <div className="flex flex-1 flex-col gap-5">
            <Pulse className="h-6 w-56" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {["w-16", "w-20", "w-16"].map((w, i) => (
                <div key={i} className="flex flex-col gap-2">
                  <Pulse className={`h-4 ${w}`} />
                  <Pulse className="h-5 w-48" />
                </div>
              ))}
            </div>
          </div>
          <Pulse className="h-9 w-32" />
        </div>
      </div>
    </div>
  );
}

export const DashboardSkeleton = () => {
  return (
    <div className="pt-24 opacity-50 sm:pt-32">
      {/* Subscription section */}
      <div className="px-4 pb-8 xl:px-0">
        <SubscriptionSkeleton />
      </div>

      {/* Available products / upgrades section */}
      <ProductListSkeleton />

      {/* Payment and billing section */}
      <div className="space-y-5 pt-10">
        <PaymentAndBillingSkeleton />
      </div>
    </div>
  );
};
